import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CpLandingShell from "../../components/CpLandingShell/CpLandingShell";
import CpButton from "../../components/CpButton/CpButton";
import CpSectionHeading from "../../components/CpSectionHeading/CpSectionHeading";
import CpAccordion from "../../components/CpAccordion/CpAccordion";
import CpIcon from "../../components/CpIcon/CpIcon";
import { FAQS, GROUPS, STAGES } from "./insightsReportsContent";

/** In-app destination for "Open Your Reports" (the Hub homepage). */
const REPORTS_PATH = "/hub";

const HIGHLIGHT = "rgba(255,200,87,.16)";

const TABLE_COLUMNS = "minmax(0, 1fr) repeat(4, clamp(52px, 8vw, 96px))";
const TABLE_PAD_X = "0 clamp(12px, 2vw, 28px)";
/** Below this width the report table scrolls inside its own container. */
const TABLE_MIN_WIDTH = 600;

const HERO_BARS = [
  { stage: "Build", width: "33.3%", count: 4 },
  { stage: "Grow", width: "66.7%", count: 8 },
  { stage: "Lead", width: "83.3%", count: 10 },
  { stage: "Scale", width: "100%", count: 12 },
];

const builtForChip: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 999,
  background: "var(--white)",
  border: "1px solid var(--border-default)",
  fontSize: 13,
  fontWeight: 600,
};

const chipLabel: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--grey-500)",
  marginRight: 4,
};

/**
 * A CpButton whose link navigates in-app through the router (keeping the
 * real href for modifier-clicks and assistive tech).
 */
const RouterCpButton: React.FC<
  React.ComponentProps<typeof CpButton> & { href: string }
> = ({ href, ...props }) => {
  const navigate = useNavigate();
  const onClick = (e: React.MouseEvent) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    e.preventDefault();
    navigate(href);
  };
  return (
    <span onClick={onClick} style={{ display: "contents" }}>
      <CpButton href={href} {...props} />
    </span>
  );
};

const InsightsReportsPage: React.FC = () => {
  const [stage, setStage] = useState(-1);

  const filters = [
    { label: "All Reports", v: -1 },
    ...STAGES.map((s, i) => ({ label: s, v: i })),
  ];

  const tableGroups = GROUPS.map((g) => ({
    key: g.key,
    icon: g.icon,
    title: g.title,
    rows: g.rows.filter((r) => stage < 0 || r.min <= stage),
  })).filter((g) => g.rows.length);
  const count = tableGroups.reduce((n, g) => n + g.rows.length, 0);
  const summary =
    stage < 0
      ? "12 reports across 4 membership stages"
      : `${count} reports available on ${STAGES[stage]}`;

  return (
    <CpLandingShell title="Insights Reports">
      <section
        data-screen-label="Hero"
        style={{
          background: "var(--cream-100)",
          padding: "clamp(56px, 7vw, 96px) 0",
        }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 440px), 1fr))",
            gap: 56,
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  borderRadius: 999,
                  background: "var(--red-50)",
                  border: "1px solid rgba(178,0,48,.15)",
                  color: "var(--red-500)",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--red-500)",
                    boxShadow: "0 0 0 3px rgba(178,0,48,.18)",
                  }}
                />
                Market Insights
              </span>
            </div>
            <h1
              style={{
                margin: "22px 0 0",
                fontSize: "clamp(52px, 7.4vw, 96px)",
                lineHeight: 0.95,
                letterSpacing: "-.04em",
                fontWeight: 800,
                color: "var(--red-500)",
              }}
            >
              Insights Reports
            </h1>
            <p
              style={{
                margin: "10px 0 0",
                fontSize: "clamp(22px, 2.4vw, 30px)",
                fontWeight: 300,
                letterSpacing: "-.015em",
                lineHeight: 1.2,
                color: "var(--ink-500)",
              }}
            >
              Your Numbers, Turned Into Answers
            </p>
            <p
              style={{
                margin: "22px 0 0",
                maxWidth: "38ch",
                fontSize: "clamp(18px, 1.6vw, 21px)",
                lineHeight: 1.5,
                color: "var(--ink-500)",
              }}
            >
              Reports show where your revenue comes from, which customers drive
              it and where your market has room to grow.
            </p>

            <div
              style={{
                marginTop: 26,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={chipLabel}>Built for</span>
              <span style={builtForChip}>Owners</span>
              <span style={builtForChip}>All Trades</span>
            </div>
            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={chipLabel}>Available on</span>
              {STAGES.map((t) => (
                <span
                  key={t}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 16px 7px 14px",
                    borderRadius: 999,
                    background: "var(--ink-500)",
                    color: "var(--cream-100)",
                    fontSize: 13,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  <CpIcon color="var(--gold-400)" name="check" size={13} />
                  {t}
                </span>
              ))}
            </div>

            <div
              style={{
                marginTop: 34,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <CpButton href="#reports" size="lg">
                See Reports by Stage
              </CpButton>
              <RouterCpButton href={REPORTS_PATH} size="lg" variant="secondary">
                Open Your Reports
              </RouterCpButton>
            </div>
          </div>

          <div
            aria-label="Reports by membership stage"
            style={{
              background: "var(--ink-500)",
              borderRadius: 24,
              boxShadow: "var(--shadow-lg)",
              overflow: "hidden",
              color: "var(--cream-100)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 24px",
                borderBottom: "1px solid var(--border-inverse)",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                Reports by membership stage
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--gold-400)",
                }}
              >
                12 Total
              </span>
            </div>
            <div
              style={{
                padding: "20px 24px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              {HERO_BARS.map((b) => (
                <div
                  key={b.stage}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px minmax(0, 1fr) 40px",
                    gap: 16,
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 800 }}>
                    {b.stage}
                  </span>
                  <div
                    style={{
                      height: 14,
                      borderRadius: 999,
                      background: "var(--ink-900)",
                    }}
                  >
                    <div
                      style={{
                        width: b.width,
                        height: "100%",
                        borderRadius: 999,
                        background: "var(--gold-400)",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      textAlign: "right",
                    }}
                  >
                    {b.count}
                  </span>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                padding: "20px 24px 24px",
                background: "var(--ink-900)",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "var(--gold-400)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <CpIcon color="var(--ink-500)" name="chart-column" size={20} />
              </div>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--cream-100)",
                }}
              >
                More reports unlock at every stage
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        data-screen-label="What Are Insights Reports"
        style={{
          background: "var(--ink-500)",
          padding: "clamp(56px, 7vw, 96px) 0",
        }}
      >
        <div
          style={{
            maxWidth: 1160,
            margin: "0 auto",
            padding: "0 24px",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: "24px 64px",
            alignItems: "start",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "var(--gold-400)",
              fontSize: "clamp(30px, 3.2vw, 42px)",
              fontWeight: 800,
              letterSpacing: "-.025em",
              lineHeight: 1.12,
            }}
          >
            What are Insights Reports?
          </h2>
          <div>
            <p
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: "clamp(20px, 1.9vw, 24px)",
                fontWeight: 300,
                lineHeight: 1.45,
              }}
            >
              Your business already has the data.{" "}
              <strong style={{ fontWeight: 800 }}>
                Insights Reports show you what it means.
              </strong>
            </p>
            <p
              style={{
                margin: "18px 0 0",
                color: "var(--cream-100)",
                fontSize: 17,
                lineHeight: 1.65,
              }}
            >
              Each report takes a single question — how is revenue trending,
              which customers turn into replacement sales, which ZIP codes drive
              results — and answers it with your own numbers. Use them to focus
              your marketing, plan for busy and off-season months, and make
              financial decisions with a clear view.
            </p>
          </div>
        </div>
      </section>

      <section
        data-screen-label="Three Questions"
        style={{ background: "var(--sand-200)", padding: "var(--section-y) 0" }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <CpSectionHeading
            align="center"
            eyebrow="What the Reports Answer"
            title="Three Questions Every Owner Should Be Able to Answer"
          />
          <div
            data-stagger=""
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
              gap: 24,
              marginTop: 48,
            }}
          >
            {GROUPS.map((g) => (
              <div
                key={g.key}
                style={{
                  background: "var(--white)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 16,
                  padding: 32,
                  boxShadow: "var(--shadow-sm)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: "var(--ink-500)",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CpIcon color="var(--gold-400)" name={g.icon} size={24} />
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: "var(--red-500)",
                    }}
                  >
                    {g.rows.length} Reports
                  </span>
                </div>
                <h3
                  style={{
                    margin: "6px 0 0",
                    fontSize: 22,
                    lineHeight: 1.25,
                    letterSpacing: "-.015em",
                  }}
                >
                  {g.question}
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: "var(--text-muted)",
                  }}
                >
                  {g.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-screen-label="Reports by Stage"
        id="reports"
        style={{ background: "var(--white)", padding: "var(--section-y) 0" }}
      >
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 24px" }}>
          <CpSectionHeading
            align="center"
            eyebrow="Reports by Stage"
            lead="Pick your membership stage to see the reports you have access to."
            title="Every Report and Where It's Available"
          />

          <div
            aria-label="Filter by membership stage"
            role="group"
            style={{
              marginTop: 36,
              display: "flex",
              justifyContent: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {filters.map((f) => {
              const on = f.v === stage;
              return (
                <button
                  aria-pressed={on}
                  key={f.label}
                  onClick={() => setStage(f.v)}
                  style={{
                    fontFamily: "inherit",
                    fontSize: 14,
                    fontWeight: 800,
                    padding: "10px 20px",
                    borderRadius: 999,
                    cursor: "pointer",
                    background: on ? "var(--ink-500)" : "var(--white)",
                    color: on ? "var(--cream-100)" : "var(--ink-500)",
                    border: `1px solid ${on ? "var(--ink-500)" : "var(--border-strong)"}`,
                    transition:
                      "background 140ms cubic-bezier(.16,1,.3,1), color 140ms cubic-bezier(.16,1,.3,1)",
                  }}
                  type="button"
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 32,
              background: "var(--white)",
              border: "1px solid var(--border-default)",
              borderRadius: 16,
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: TABLE_COLUMNS,
                    alignItems: "center",
                    background: "var(--ink-500)",
                    color: "var(--cream-100)",
                    padding: TABLE_PAD_X,
                  }}
                >
                  <span
                    style={{
                      padding: "18px 0",
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: "var(--gold-400)",
                    }}
                  >
                    Report
                  </span>
                  {STAGES.map((s, i) => (
                    <span
                      key={s}
                      style={{
                        padding: "18px 0",
                        textAlign: "center",
                        fontSize: 14,
                        fontWeight: 800,
                        background:
                          i === stage ? "var(--gold-400)" : "transparent",
                        color:
                          i === stage ? "var(--ink-500)" : "var(--cream-100)",
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {tableGroups.map((tg) => (
                  <div key={tg.key}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "14px clamp(12px, 2vw, 28px)",
                        background: "var(--cream-100)",
                        borderTop: "1px solid var(--border-default)",
                        borderBottom: "1px solid var(--border-default)",
                      }}
                    >
                      <CpIcon color="var(--red-500)" name={tg.icon} size={18} />
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          letterSpacing: ".14em",
                          textTransform: "uppercase",
                          color: "var(--red-500)",
                        }}
                      >
                        {tg.title}
                      </span>
                    </div>
                    {tg.rows.map((r) => (
                      <div
                        key={r.name}
                        style={{
                          display: "grid",
                          gridTemplateColumns: TABLE_COLUMNS,
                          alignItems: "stretch",
                          padding: TABLE_PAD_X,
                          borderBottom: "1px solid var(--border-default)",
                        }}
                      >
                        <div style={{ padding: "20px 16px 20px 0" }}>
                          <div
                            style={{
                              fontSize: 17,
                              fontWeight: 800,
                              lineHeight: 1.3,
                              letterSpacing: "-.01em",
                            }}
                          >
                            {r.name}
                          </div>
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 15,
                              lineHeight: 1.5,
                              color: "var(--text-muted)",
                              textWrap: "pretty",
                            }}
                          >
                            {r.desc}
                          </div>
                        </div>
                        {STAGES.map((s, i) => (
                          <div
                            key={s}
                            style={{
                              display: "grid",
                              placeItems: "center",
                              background:
                                i === stage ? HIGHLIGHT : "transparent",
                            }}
                          >
                            {i >= r.min ? (
                              <span
                                aria-label="Available"
                                style={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: "50%",
                                  background: "var(--ink-500)",
                                  display: "grid",
                                  placeItems: "center",
                                }}
                              >
                                <CpIcon
                                  color="var(--gold-400)"
                                  name="check"
                                  size={16}
                                />
                              </span>
                            ) : (
                              <span
                                aria-label="Not available"
                                style={{
                                  width: 14,
                                  height: 2,
                                  borderRadius: 2,
                                  background: "var(--grey-300)",
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                padding: "18px clamp(12px, 2vw, 28px)",
                background: "var(--cream-100)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 700 }}>{summary}</span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--grey-500)",
                }}
              >
                Higher stages include every report from the stages before them.
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        data-screen-label="FAQ"
        id="faq"
        style={{
          background: "var(--sand-200)",
          padding: "var(--section-y) 0 48px",
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px" }}>
          <CpSectionHeading
            align="center"
            eyebrow="Questions"
            title="Frequently Asked Questions"
          />
          <div style={{ marginTop: 40 }}>
            <CpAccordion items={FAQS} />
          </div>
        </div>
      </section>

      <section
        data-screen-label="CTA"
        style={{
          background: "var(--sand-200)",
          padding: "48px 0 var(--section-y)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              background: "var(--gradient-brand)",
              borderRadius: 24,
              padding: "clamp(56px, 7vw, 88px) clamp(24px, 5vw, 56px)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 18,
              boxShadow: "var(--shadow-lg)",
            }}
          >
            <h2
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: "var(--text-display-2)",
                lineHeight: 1.08,
                letterSpacing: "-.025em",
                maxWidth: "20ch",
                textWrap: "balance",
              }}
            >
              Know Your Numbers. Know Your Market.
            </h2>
            <p
              style={{
                margin: "0 0 14px",
                color: "var(--cream-100)",
                fontSize: 18,
                lineHeight: 1.55,
                maxWidth: "54ch",
              }}
            >
              Your Insights Reports are waiting. Open them and see what your
              data is telling you.
            </p>
            <RouterCpButton href={REPORTS_PATH} size="lg" variant="inverse">
              Open Your Reports
            </RouterCpButton>
          </div>
        </div>
      </section>
    </CpLandingShell>
  );
};

export default InsightsReportsPage;
